import React,{
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import {getPostLayout} from '../data/postLayouts';
import {
  buildOverrideTransform,
  getLayoutOverride
} from '../data/layoutOverrides';
import {
  fitTextToBounds,
  measureRenderedText
} from '../lib/fitTextToBounds';
import './PostLayouts.css';

const LEDGER_CANVAS={
  square:{width:1080,height:1080},
  portrait:{width:1080,height:1350}
};

const LEDGER_FORMAT={
  square:{
    spineWidthPct:0.17,
    photoZoneHeightPct:0.42,
    headlineStartPx:58
  },
  portrait:{
    spineWidthPct:0.19,
    photoZoneHeightPct:0.44,
    headlineStartPx:64
  }
};

const LEDGER_SHARED={
  spinePaddingPct:0.045,
  contentPadTopPct:0.06,
  contentPadHorizontalPct:0.055,
  blockBudgetPct:0.55,
  dividerHeightPct:0.01,
  dividerMarginRefPx:33,
  dividerWidthPct:0.155,
  photoBleedPct:0.13,
  headlineLineHeight:0.96,
  headlineLetterSpacing:'-0.04em',
  subheadLineHeight:1.45,
  subheadFontSizePct:0.0185
};

function resolveCanvasFormat(post,canvasFormatProp) {
  const format=canvasFormatProp || post?.canvasFormat || 'square';
  return format==='portrait' ? 'portrait' : 'square';
}

function computeLedgerHeadlineFit({
  canvasWidth,
  canvasHeight,
  canvasFormat,
  headline,
  supporting,
  showSupporting,
  displayFont,
  bodyFont,
  subheadFontScale
}) {
  const formatConfig=LEDGER_FORMAT[canvasFormat];
  const spineWidth=canvasWidth*formatConfig.spineWidthPct;
  const contentWidth=canvasWidth-spineWidth;
  const contentPadHorizontal=canvasWidth*LEDGER_SHARED.contentPadHorizontalPct;
  const contentPadTop=canvasHeight*LEDGER_SHARED.contentPadTopPct;
  const contentAreaHeight=canvasHeight-contentPadTop;
  const blockBudget=contentAreaHeight*LEDGER_SHARED.blockBudgetPct;
  const headlineMaxWidth=contentWidth-(2*contentPadHorizontal);
  const dividerHeight=canvasHeight*LEDGER_SHARED.dividerHeightPct;
  const dividerMargin=(LEDGER_SHARED.dividerMarginRefPx/1080)*canvasHeight;
  const dividerAllowance=dividerHeight+(dividerMargin*2);
  const subheadFontSize=Math.round(
    canvasWidth*LEDGER_SHARED.subheadFontSizePct*subheadFontScale
  );
  let subheadAllowance=0;

  if (showSupporting) {
    subheadAllowance=measureRenderedText({
      text:supporting,
      fontFamily:`'${bodyFont}',sans-serif`,
      fontSize:subheadFontSize,
      lineHeight:LEDGER_SHARED.subheadLineHeight,
      maxWidth:headlineMaxWidth
    }).height;
  }

  const refCanvas=LEDGER_CANVAS[canvasFormat];
  const scale=canvasWidth/refCanvas.width;
  const headlineMaxHeight=Math.max(
    0,
    blockBudget-dividerAllowance-subheadAllowance
  );

  return fitTextToBounds({
    text:headline,
    fontFamily:`'${displayFont}',serif`,
    startFontSize:Math.round(formatConfig.headlineStartPx*scale),
    maxWidth:headlineMaxWidth,
    maxHeight:headlineMaxHeight,
    lineHeight:LEDGER_SHARED.headlineLineHeight,
    letterSpacing:LEDGER_SHARED.headlineLetterSpacing
  });
}

const PostArtwork=forwardRef(function PostArtwork({
  post,
  brand,
  compact=false,
  layoutOverride=null,
  canvasFormat=null
},ref) {
  const layout=getPostLayout(post);
  const isLedger=layout==='ledger';
  const isQuote=layout==='quote';
  const isSplit=layout==='split';
  const isFullBleed=layout==='fullbleed';
  const isFramed=layout==='framed';
  const showSupporting=Boolean(post.supporting?.trim());
  const displayFont=brand.displayFont || 'DM Serif Display';
  const bodyFont=brand.bodyFont || 'DM Sans';
  const resolvedCanvasFormat=resolveCanvasFormat(post,canvasFormat);
  const overrides=layoutOverride ?? getLayoutOverride(brand?.layoutOverrides,layout);
  const {
    logoScale,
    logoOffsetX,
    logoOffsetY,
    imageScale,
    imageOffsetX,
    imageOffsetY,
    headlineFontScale,
    subheadFontScale
  }=overrides;

  const canvasRef=useRef(null);
  const [ledgerHeadline,setLedgerHeadline]=useState({
    fontSize:LEDGER_FORMAT.square.headlineStartPx,
    didFit:true
  });

  const setCanvasRef=useCallback((node)=> {
    canvasRef.current=node;

    if (typeof ref==='function') {
      ref(node);
      return;
    }

    if (ref) {
      ref.current=node;
    }
  },[ref]);

  useLayoutEffect(()=> {
    if (!isLedger || !canvasRef.current) {
      return;
    }

    const {width,height}=canvasRef.current.getBoundingClientRect();

    if (!width || !height) {
      return;
    }

    setLedgerHeadline(
      computeLedgerHeadlineFit({
        canvasWidth:width,
        canvasHeight:height,
        canvasFormat:resolvedCanvasFormat,
        headline:post.headline,
        supporting:post.supporting,
        showSupporting,
        displayFont,
        bodyFont,
        subheadFontScale
      })
    );
  },[
    isLedger,
    post.headline,
    post.supporting,
    showSupporting,
    displayFont,
    bodyFont,
    resolvedCanvasFormat,
    subheadFontScale,
    compact
  ]);

  const logoTransform=buildOverrideTransform(
    logoScale,
    logoOffsetX,
    logoOffsetY
  );
  const imageTransform=buildOverrideTransform(
    imageScale,
    imageOffsetX,
    imageOffsetY
  );
  const headlineTransform=headlineFontScale===1
    ? undefined
    : `scale(${headlineFontScale})`;
  const subheadTransform=subheadFontScale===1
    ? undefined
    : `scale(${subheadFontScale})`;
  const headlineOrigin=isQuote || isFullBleed ? 'center top' : 'left top';
  const subheadOrigin=isQuote || isFullBleed ? 'center top' : 'left top';
  const kickerText=post.kicker?.trim() || '';
  const showPhoto=Boolean(post.image?.trim());

  const artworkStyle={
    '--post-brand':brand.primary,
    '--post-accent':brand.accent
  };

  if (isFullBleed) {
    artworkStyle.background=brand.primary;
  }

  if (isLedger) {
    artworkStyle['--ledger-headline-size']=`${ledgerHeadline.fontSize}px`;
  }

  const artworkClassName=[
    'post-artwork',
    `layout-${layout}`,
    `canvas-${resolvedCanvasFormat}`,
    compact ? 'compact' : '',
    isLedger && !ledgerHeadline.didFit ? 'ledger-headline-overflow' : ''
  ].filter(Boolean).join(' ');

  if (isLedger) {
    return (
      <div
        ref={setCanvasRef}
        className={artworkClassName}
        style={artworkStyle}
      >
        <div className="ledger-spine">
          {kickerText && (
            <div
              className="ledger-kicker"
              style={{fontFamily:`'${displayFont}',serif`}}
            >
              {kickerText}
            </div>
          )}
          <div className="ledger-spine-logo">
            <span
              className="mini-logo"
              style={{
                borderColor:brand.accent,
                overflow:'hidden',
                transform:logoTransform,
                transformOrigin:'center center'
              }}
            >
              {brand.logo ? (
                <img
                  src={brand.logo}
                  alt=""
                  crossOrigin="anonymous"
                  style={{width:'100%',height:'100%',objectFit:'cover'}}
                />
              ) : brand.monogram}
            </span>
          </div>
        </div>

        <div className="ledger-content">
          <div className="ledger-copy-block">
            <h2
              className="ledger-headline"
              style={{
                fontFamily:`'${displayFont}',serif`,
                transform:headlineTransform,
                transformOrigin:'left top'
              }}
            >
              {post.headline}
            </h2>
            {!ledgerHeadline.didFit && (
              <p className="ledger-headline-warning" role="status">
                Headline is too long for this layout
              </p>
            )}
            <div className="ledger-divider" aria-hidden="true" />
            {showSupporting && (
              <div
                className="ledger-subhead-wrap"
                style={{
                  transform:subheadTransform,
                  transformOrigin:'left top'
                }}
              >
                <p
                  className="ledger-subhead"
                  style={{fontFamily:`'${bodyFont}',sans-serif`}}
                >
                  {post.supporting}
                </p>
              </div>
            )}
          </div>

          {showPhoto && (
            <div className="ledger-photo-zone">
              <div
                className="ledger-photo"
                style={{
                  backgroundImage:`url("${post.image}")`,
                  transform:imageTransform
                }}
              />
              <div className="ledger-photo-gradient" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setCanvasRef}
      className={artworkClassName}
      style={artworkStyle}
    >
      {!isFullBleed && !isFramed && (
        <div
          className="artwork-photo"
          style={{
            backgroundImage:`url("${post.image}")`,
            transform:imageTransform
          }}
        />
      )}
      {!isFullBleed && !isFramed && <div className="artwork-shade" />}
      {isSplit && <div className="split-panel" />}
      {isFramed && (
        <div
          className="framed-photo"
          style={{
            backgroundImage:`url("${post.image}")`,
            transform:imageTransform
          }}
        />
      )}
      <div className="artwork-brand" style={{fontFamily:`'${bodyFont}',sans-serif`}}>
        <span
          className="mini-logo"
          style={{
            borderColor:brand.accent,
            overflow:'hidden',
            transform:logoTransform,
            transformOrigin:'center center'
          }}
        >
          {brand.logo ? (
            <img
              src={brand.logo}
              alt=""
              crossOrigin="anonymous"
              style={{width:'100%',height:'100%',objectFit:'cover'}}
            />
          ) : brand.monogram}
        </span>
        <span>{brand.name}</span>
      </div>
      <div className="artwork-copy">
        {isQuote ? (
          <span className="quote-mark" aria-hidden="true">“</span>
        ) : (
          !isFullBleed && !isFramed && <i style={{background:brand.accent}} />
        )}
        <h2
          style={{
            fontFamily:`'${displayFont}',serif`,
            transform:headlineTransform,
            transformOrigin:headlineOrigin
          }}
        >
          {post.headline}
        </h2>
        {showSupporting && (
          <div
            className="artwork-subhead-wrap"
            style={{
              transform:subheadTransform,
              transformOrigin:subheadOrigin
            }}
          >
            <p
              className="artwork-subhead"
              style={{fontFamily:`'${bodyFont}',sans-serif`}}
            >
              {post.supporting}
            </p>
          </div>
        )}
      </div>
      <div className="artwork-footer" style={{fontFamily:`'${bodyFont}',sans-serif`}}>
        Perspective · Planning · Progress
      </div>
    </div>
  );
});

export default PostArtwork;
